/**
 * Browsers cap how large a `<canvas>` backing store may be, both per side and by
 * total area. Past the cap the element becomes unpaintable: Chrome draws a
 * broken-image placeholder and every 2D operation silently no-ops — no exception
 * is thrown, so nothing reaches Sentry.
 *
 * The designer sizes its canvas to the whole document, and fabric multiplies the
 * backing store by `devicePixelRatio` (see `__initRetinaScaling`). A ~49k px tall
 * document therefore survives at dpr 1 but dies at dpr 2, which is exactly how
 * the same design can render for one user and break for another.
 */

/** Total backing-store area every known engine tops out at (32768 * 8192). */
export const MAX_CANVAS_AREA = 268_435_456;

/**
 * Used when the per-side limit cannot be measured (jsdom, or a browser that
 * blocks `getImageData`). Deliberately generous: guessing too low would clamp
 * documents that render fine today, which is a regression. Guessing too high
 * merely leaves behaviour as it is now.
 */
export const FALLBACK_MAX_DIMENSION = 65_535;

/** Above any per-side limit shipped by a real engine, so the probe brackets it. */
const PROBE_CEILING = 131_072;

/** A measured limit below this is treated as a broken probe, not a real answer. */
const MIN_PLAUSIBLE_MAX_DIMENSION = 4_096;

let cachedMaxDimension: number | undefined;

/**
 * Whether the browser actually gives us a usable surface at this size. A context
 * object is not proof — oversized canvases still hand back a context whose draw
 * calls do nothing, so we write a pixel and read it back.
 */
function canAllocate(width: number, height: number): boolean {
  try {
    const element = document.createElement("canvas");
    element.width = width;
    element.height = height;

    const context = element.getContext("2d");
    if (!context) {
      return false;
    }

    context.fillStyle = "#ff0000";
    context.fillRect(0, 0, 1, 1);

    return context.getImageData(0, 0, 1, 1).data[0] === 255;
  } catch {
    return false;
  }
}

/**
 * Measures the per-side limit once per session by binary search. Probes are 1px
 * wide so they stay far below the area limit and cost almost no memory, which
 * isolates the per-side cap from the area cap.
 *
 * Fails open — an unmeasurable environment gets {@link FALLBACK_MAX_DIMENSION}
 * rather than a small number that would clamp working documents.
 */
export function getMaxCanvasDimension(): number {
  if (cachedMaxDimension !== undefined) {
    return cachedMaxDimension;
  }

  if (!canAllocate(1, 1)) {
    // Not a real canvas implementation — don't trust anything it reports.
    cachedMaxDimension = FALLBACK_MAX_DIMENSION;
    return cachedMaxDimension;
  }

  if (canAllocate(1, PROBE_CEILING)) {
    cachedMaxDimension = PROBE_CEILING;
    return cachedMaxDimension;
  }

  let supported = 1;
  let unsupported = PROBE_CEILING;
  while (supported < unsupported - 1) {
    const middle = Math.floor((supported + unsupported) / 2);
    if (canAllocate(1, middle)) {
      supported = middle;
    } else {
      unsupported = middle;
    }
  }

  cachedMaxDimension =
    supported >= MIN_PLAUSIBLE_MAX_DIMENSION
      ? supported
      : FALLBACK_MAX_DIMENSION;

  return cachedMaxDimension;
}

/** Test seam — lets specs pin or reset the measured value. */
export function setMaxCanvasDimensionForTesting(value?: number): void {
  cachedMaxDimension = value;
}

/**
 * Largest CSS height whose backing store still fits the browser's limits.
 *
 * Returns `height` untouched whenever it already fits, so documents that render
 * today are unaffected: the clamp can only bind on a canvas that is already dead.
 * Only ever reduces — the export route mounts a deliberately zero-height canvas
 * and must keep it.
 *
 * @param width CSS width of the canvas
 * @param height CSS height we would like to apply
 * @param dpr device pixel ratio fabric will multiply the backing store by
 * @param maxDimension per-side limit, from {@link getMaxCanvasDimension}
 */
export function clampCanvasHeight(
  width: number,
  height: number,
  dpr: number,
  maxDimension: number,
): number {
  const ratio = dpr > 0 ? dpr : 1;

  // Backing store is width*ratio by height*ratio, so area grows with ratio squared.
  const areaLimited = Math.floor(MAX_CANVAS_AREA / (width * ratio * ratio));
  const sideLimited = Math.floor(maxDimension / ratio);

  return Math.max(0, Math.min(height, sideLimited, areaLimited));
}
